import { sortBy } from 'lodash';
import {
  CapitalisationDateAttributionType,
  DepenseType,
  PrejudiceFormCalendrierDepense,
  PrejudiceFormCalendrierDepenseCapitalisationRow,
  PrejudiceFormCalendrierDepenseRow,
} from 'src/types/prejudice.type';
import { Procedure } from 'src/types/procedure.type';
import { Victime } from 'src/types/victime.type';
import { prejudiceBaseDefaultValues } from '../defaultValues';

export type CalendrierDepenseExistingRow = Partial<PrejudiceFormCalendrierDepenseRow>;
export type CalendrierDepenseExistingValues = Omit<Partial<PrejudiceFormCalendrierDepense>, 'rows'> & {
  rows?: CalendrierDepenseExistingRow[];
};
export const defaultTableDepenseRow = ({
  tiersPayeurs,
  hidePeriode,
  type,
  isCalculCapitalisation,
  procedure,
  victime,
  dateAttributionType,
}: {
  tiersPayeurs: string[];
  hidePeriode?: boolean;
  type: DepenseType;
  isCalculCapitalisation?: boolean;
  procedure: Procedure;
  victime: Partial<Victime>;
  dateAttributionType?: CapitalisationDateAttributionType;
}):
  | PrejudiceFormCalendrierDepenseRow
  | PrejudiceFormCalendrierDepenseCapitalisationRow => {
  const initialTiersPayeursValues = tiersPayeurs.map(
    (tiersPayeur) => ({ tiersPayeur, montant: 0 }),
    {},
  );
  const { dateAccident, dateDeces } = victime;
  const { dateConsolidation, dateLiquidation } = procedure;
  const defaultDateDebut =
    type === 'recurrente' && !isCalculCapitalisation
      ? dateAccident || null
      : type === 'recurrente' && isCalculCapitalisation
        ? dateConsolidation || null
        : null;
  const defaultDateFin =
    type === 'ponctuelle'
      ? null
      : !isCalculCapitalisation
        ? dateConsolidation || dateDeces || dateLiquidation || null
        : null;
  return {
    type,
    intitule: '',
    dateDebut: hidePeriode ? undefined : defaultDateDebut,
    dateFin: hidePeriode ? undefined : defaultDateFin,
    dureeDansUniteChoisie: 1,
    frequenceMontant: 'non_renseigne',
    montantUnitaire: 0,
    montantTotal: 0,
    quantite: 1,
    dateJustificatif: defaultDateDebut,
    montantsDejaRevalorises: false,
    priseEnChargeTiersPayeurs: initialTiersPayeursValues,
    priseEnChargeTiersPayeursProratises: initialTiersPayeursValues,
    resteACharge: 0,
    renouvellementMaintenance: 0,
    numerosPieces: { rows: [] },
    capitalisation: {
      isLastArrerageViager: false,
      ageDernierArrerage: null,
      coefficient: null,
      montantCapitalise: 0,
      dateAttribution: null,
      dateAttributionType: dateAttributionType,
    },
    capitalisationTiersPayeurs: {
      parTiersPayeur: tiersPayeurs.map((tiersPayeur) => ({
        tiersPayeur,
        sommeACapitaliser: 0,
        coefficient: 0,
        montantCapitalise: 0,
      })),
    },
  };
};

export const calendrierDepenseDefaultValues = ({
  procedure,
  victime,
  values,
  hidePeriode,
  multiplyPartResponsabilite,
  isVictimeIndirecte,
}: {
  procedure: Procedure;
  victime: Partial<Victime>;
  values?: CalendrierDepenseExistingValues;
  hidePeriode?: boolean;
  multiplyPartResponsabilite: boolean;
  isVictimeIndirecte: boolean;
}): PrejudiceFormCalendrierDepense => ({
  ...prejudiceBaseDefaultValues({
    values,
  }),
  notes: values?.notes || '',
  rows: sortBy(values?.rows || [], (row) => row.dateDebut).map((row) => ({
    ...defaultTableDepenseRow({
      tiersPayeurs: procedure.tiersPayeurs,
      type: row.type ?? 'ponctuelle',
      isCalculCapitalisation: false,
      hidePeriode,
      procedure,
      victime,
    }),
    ...row,
    priseEnChargeTiersPayeurs: procedure.tiersPayeurs.map((tiersPayeur) => ({
      tiersPayeur,
      montant:
        (Array.isArray(row.priseEnChargeTiersPayeurs)
          ? row.priseEnChargeTiersPayeurs
          : []
        ).find(
          (priseEnChargeTiersPayeur) =>
            priseEnChargeTiersPayeur.tiersPayeur === tiersPayeur,
        )?.montant || 0,
    })),
    priseEnChargeTiersPayeursProratises: procedure.tiersPayeurs.map(
      (tiersPayeur) => ({
        tiersPayeur,
        montant:
          (Array.isArray(row.priseEnChargeTiersPayeursProratises)
            ? row.priseEnChargeTiersPayeursProratises
            : []
          ).find(
            (priseEnChargeTiersPayeur) =>
              priseEnChargeTiersPayeur.tiersPayeur === tiersPayeur,
          )?.montant || 0,
      }),
    ),
  })),
  revalorisationCoefficientsType:
    values?.revalorisationCoefficientsType || 'annuel',
  partResponsabilite:
    (!isVictimeIndirecte
      ? procedure.partResponsabilite
      : values?.partResponsabilite || values?.partResponsabilite === 0
        ? values?.partResponsabilite
        : procedure.partResponsabilite) *
    (multiplyPartResponsabilite ? 100 : 1),
});
